import React, { useState } from "react";
import { Button } from "../ui/components/Button";
import { FeatherX, FeatherArrowRight } from "@subframe/core";
import { GSTR2BRecord } from "../types/gstr2bTypes";
import { PurchaseRegisterInvoice } from "../types/gstr2bTypes";

interface MatchConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  gstr2bRecord: GSTR2BRecord;
  selectedInvoice: PurchaseRegisterInvoice;
  onConfirm: (notes: string) => Promise<void>;
}

const MatchConfirmationModal: React.FC<MatchConfirmationModalProps> = ({
  isOpen,
  onClose,
  gstr2bRecord,
  selectedInvoice,
  onConfirm,
}) => {
  const [notes, setNotes] = useState('');
  const [confirming, setConfirming] = useState(false);

  // Format currency
  const formatCurrency = (amount: number): string => {
    return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      await onConfirm(notes || 'Manual match - invoice numbers or amounts differ');
      onClose();
    } finally {
      setConfirming(false);
    }
  };

  if (!isOpen) return null;

  const amountDiff = Math.abs(gstr2bRecord.gstr2bAmount - selectedInvoice.totalAmount);
  const hasSignificantDiff = amountDiff > 100;

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-70 z-[70] flex items-center justify-center"
        onClick={onClose}
      >
        {/* Modal Content */}
        <div 
          className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-neutral-border px-6 py-4">
            <span className="text-heading-2 font-heading-2 text-default-font">
              Confirm Manual Match
            </span>
            <button
              onClick={onClose}
              className="text-subtext-color hover:text-default-font transition-colors"
            >
              <FeatherX className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-6">
            {/* Comparison View */}
            <div className="flex items-center gap-6 mb-6">
              {/* GSTR-2B Invoice */}
              <div className="flex-1 p-4 border border-neutral-border rounded-lg bg-neutral-50">
                <div className="text-caption font-caption text-subtext-color mb-2">
                  GSTR-2B Entry
                </div>
                <div className="flex flex-col gap-2">
                  <div>
                    <div className="text-caption font-caption text-subtext-color">
                      Invoice Number
                    </div>
                    <div className="text-body-bold font-body-bold text-default-font">
                      {gstr2bRecord.invoiceNumber}
                    </div>
                  </div>
                  <div>
                    <div className="text-caption font-caption text-subtext-color">
                      Date
                    </div>
                    <div className="text-body font-body text-default-font">
                      {gstr2bRecord.invoiceDate}
                    </div>
                  </div>
                  <div>
                    <div className="text-caption font-caption text-subtext-color">
                      Total Amount
                    </div>
                    <div className="text-body-bold font-body-bold text-default-font">
                      {formatCurrency(gstr2bRecord.gstr2bAmount)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Arrow */}
              <div className="flex items-center justify-center">
                <div className="p-3 bg-brand-50 rounded-full">
                  <FeatherArrowRight className="h-6 w-6 text-brand-600" />
                </div>
              </div>

              {/* Purchase Register Invoice */}
              <div className="flex-1 p-4 border border-neutral-border rounded-lg bg-neutral-50">
                <div className="text-caption font-caption text-subtext-color mb-2">
                  Books Entry
                </div>
                <div className="flex flex-col gap-2">
                  <div>
                    <div className="text-caption font-caption text-subtext-color">
                      Invoice Number
                    </div>
                    <div className="text-body-bold font-body-bold text-default-font">
                      {selectedInvoice.invoiceNumber}
                    </div>
                  </div>
                  <div>
                    <div className="text-caption font-caption text-subtext-color">
                      Date
                    </div>
                    <div className="text-body font-body text-default-font">
                      {selectedInvoice.invoiceDate}
                    </div>
                  </div>
                  <div>
                    <div className="text-caption font-caption text-subtext-color">
                      Total Amount
                    </div>
                    <div className="text-body-bold font-body-bold text-default-font">
                      {formatCurrency(selectedInvoice.totalAmount)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Warning if significant difference */}
            {hasSignificantDiff && (
              <div className="mb-4 p-3 bg-warning-50 border border-warning-200 rounded-lg">
                <span className="text-body font-body text-warning-700">
                  ⚠️ Warning: Amounts differ by {formatCurrency(amountDiff)}
                </span>
              </div>
            )}

            {/* Notes Input */}
            <div className="mb-4">
              <label className="block text-body-bold font-body-bold text-default-font mb-2">
                Notes (Optional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any notes about this manual match (e.g., 'Invoice number format different', 'Amount includes additional charges')"
                className="w-full px-3 py-2 border border-neutral-border rounded-lg text-body font-body resize-none"
                rows={3}
              />
            </div>

            {/* Info */}
            <div className="text-caption font-caption text-subtext-color">
              This will create a manual match between the GSTR-2B invoice and the selected purchase register entry. 
              Both entries will be marked as reconciled.
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 border-t border-neutral-border px-6 py-4">
            <Button
              variant="neutral-secondary"
              onClick={onClose}
              disabled={confirming}
            >
              Cancel
            </Button>
            <Button
              variant="brand-primary"
              onClick={handleConfirm}
              disabled={confirming}
              loading={confirming}
            >
              {confirming ? 'Confirming...' : 'Confirm Match'}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default MatchConfirmationModal;

